import type { BillingInvoiceSummary } from '@fruitful/types';
import * as Linking from 'expo-linking';
import { ArrowRightIcon, BodySmallRegular, Icon, Row, XStack } from 'smoothie';

import { centsToDollars, formatDollars } from '../../common/utils/bankingUtils';
import { format } from '../../common/utils/dateUtils';

const formatInvoiceDate = (date: string): string => format(date, 'MMMM d');

const formatInvoiceRowDate = (date: string): string =>
    format(date, 'MMMM d, yyyy');

const formatInvoiceAmount = (cents: number): string =>
    formatDollars(centsToDollars(cents), cents % 100 !== 0);

const getInvoiceRowCopy = (
    invoice: BillingInvoiceSummary,
): { title: string; subtitle: string } => {
    const title = formatInvoiceRowDate(invoice.issuedAt);

    switch (invoice.displayStatus) {
        case 'paid':
            return {
                title,
                subtitle: `${formatInvoiceAmount(invoice.amountPaidCents)} • Paid`,
            };
        case 'needs_attention': {
            const amountCents =
                invoice.amountRemainingCents > 0
                    ? invoice.amountRemainingCents
                    : invoice.amountDueCents;
            const nextAttempt =
                invoice.nextPaymentAttemptAt != null
                    ? ` • Next attempt ${formatInvoiceDate(invoice.nextPaymentAttemptAt)}`
                    : '';
            return {
                title,
                subtitle: `${formatInvoiceAmount(amountCents)} • Unsuccessful${nextAttempt}`,
            };
        }
        case 'processing':
            return {
                title,
                subtitle: `${formatInvoiceAmount(invoice.amountDueCents)} • Pending`,
            };
        case 'no_charge':
            return {
                title,
                subtitle: 'No charge',
            };
        case 'void':
            return {
                title,
                subtitle: `${formatInvoiceAmount(invoice.amountDueCents)} • Voided`,
            };
        case 'uncollectible':
            return {
                title,
                subtitle: `${formatInvoiceAmount(invoice.amountDueCents)} • Not collected`,
            };
        default: {
            const unexpected: never = invoice.displayStatus;
            return { title: unexpected, subtitle: '' };
        }
    }
};

export const BillingInvoiceRow = ({
    invoice,
}: {
    invoice: BillingInvoiceSummary;
}) => {
    const copy = getInvoiceRowCopy(invoice);
    const pdfUrl = invoice.invoicePdfUrl;

    return (
        <Row
            title={copy.title}
            subtitle={copy.subtitle}
            containerProps={{ paddingVertical: 16 }}
            {...(pdfUrl != null
                ? {
                      onPress: () => {
                          void Linking.openURL(pdfUrl);
                      },
                      rightContent: (
                          <XStack alignItems="center" gap={4}>
                              <BodySmallRegular color="$gray1100">
                                  View
                              </BodySmallRegular>
                              <Icon
                                  icon={ArrowRightIcon}
                                  size={16}
                                  color="$gray1100"
                              />
                          </XStack>
                      ),
                  }
                : {})}
        />
    );
};
